import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { Crown, Languages, LoaderCircle, MessageSquarePlus, SendHorizonal, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { API, useAuth } from "@/context/AuthContext";
import { TARGETS } from "@/lib/langs";
import { EASE } from "@/components/landing/Reveal";

const MD_COMPONENTS = {
  p: (props) => <p className="mb-2 last:mb-0 leading-relaxed" {...props} />,
  ul: (props) => <ul className="mb-2 list-disc space-y-1 pl-5" {...props} />,
  ol: (props) => <ol className="mb-2 list-decimal space-y-1 pl-5" {...props} />,
  li: (props) => <li className="leading-relaxed" {...props} />,
  strong: (props) => <strong className="font-bold" {...props} />,
  h1: (props) => <p className="mb-1.5 mt-2 text-sm font-bold" {...props} />,
  h2: (props) => <p className="mb-1.5 mt-2 text-sm font-bold" {...props} />,
  h3: (props) => <p className="mb-1 mt-2 text-[13px] font-bold" {...props} />,
  a: (props) => <a className="font-semibold underline" target="_blank" rel="noreferrer" {...props} />,
  code: (props) => <code className="rounded bg-black/10 px-1 text-[12px]" {...props} />,
};

function TranslateRow({ content }) {
  const { user } = useAuth();
  const [lang, setLang] = useState(user?.preferred_lang || "en");
  const [translation, setTranslation] = useState(null);
  const [loading, setLoading] = useState(false);

  const translate = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/interpreter", { text: content.slice(0, 6000), target: lang });
      setTranslation(data);
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error(err.response.data.detail, {
          action: { label: "Passer à Premium", onClick: () => (window.location.href = "/dashboard/parametres") },
        });
      } else toast.error("Traduction impossible. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-2.5 border-t border-slate-100 pt-2">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={translate} disabled={loading}
          className="flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1 text-[11px] font-semibold text-slate-500 transition-colors hover:border-bleu/40 hover:text-bleu disabled:opacity-50"
          data-testid="chat-translate-button">
          {loading ? <LoaderCircle className="h-3 w-3 animate-spin" /> : <Languages className="h-3 w-3" />} Traduire
        </button>
        <select value={lang} onChange={(e) => { setLang(e.target.value); setTranslation(null); }}
          className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-500 focus:outline-none"
          data-testid="chat-translate-lang-select">
          {TARGETS.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
        </select>
      </div>
      {translation && (
        <div dir={translation.target === "ar" ? "rtl" : "ltr"}
          className="mt-2 rounded-xl bg-bleu-light/70 p-3 text-[12.5px] leading-relaxed text-slate-700"
          data-testid="chat-translation">
          <ReactMarkdown components={MD_COMPONENTS}>{translation.translation}</ReactMarkdown>
        </div>
      )}
    </div>
  );
}

function Bubble({ role, content, accent, streaming }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${role}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-[13px] sm:max-w-[75%] ${
        isUser ? `${accent.userBubble} text-white rounded-br-md` : "bg-white border border-slate-100 text-slate-800 shadow-sm rounded-bl-md"
      }`}>
        {isUser ? <p className="whitespace-pre-wrap leading-relaxed">{content}</p> : (
          <>
            <ReactMarkdown components={MD_COMPONENTS}>{content}</ReactMarkdown>
            {streaming && <span className={`ml-1 inline-block h-3.5 w-1.5 animate-pulse rounded-sm ${accent.cursor}`} />}
            {!streaming && content && <TranslateRow content={content} />}
          </>
        )}
      </div>
    </div>
  );
}

export default function ChatAgent({ agent, title, subtitle, icon: Icon, accent, suggestions }) {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [quota, setQuota] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const loadSessions = async () => {
    const { data } = await api.get(`/chat/${agent}/sessions`);
    setSessions(data.sessions);
    setQuota(data.quota);
    return data.sessions;
  };

  useEffect(() => {
    setActiveId(null);
    setMessages([]);
    setLoading(true);
    loadSessions().then((s) => {
      if (s.length > 0) selectSession(s[0].id);
      else setLoading(false);
    }).catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const selectSession = async (id) => {
    setActiveId(id);
    setLoading(true);
    try {
      const { data } = await api.get(`/chat/sessions/${id}/messages`);
      setMessages(data);
    } catch {
      toast.error("Impossible de charger la conversation.");
    } finally {
      setLoading(false);
    }
  };

  const newSession = () => {
    setActiveId(null);
    setMessages([]);
  };

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/chat/sessions/${id}`);
      const fresh = sessions.filter((s) => s.id !== id);
      setSessions(fresh);
      if (activeId === id) newSession();
      toast.success("Conversation supprimée.");
    } catch {
      toast.error("Impossible de supprimer la conversation.");
    }
  };

  const send = async (text) => {
    const content = (text || input).trim();
    if (!content || sending) return;
    setInput("");
    setSending(true);
    let sid = activeId;
    try {
      if (!sid) {
        const { data } = await api.post(`/chat/${agent}/sessions`);
        sid = data.id;
        setActiveId(sid);
      }
      setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content }, { id: `a-${Date.now()}`, role: "assistant", content: "" }]);

      const res = await fetch(`${API}/chat/sessions/${sid}/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: content }),
      });
      if (res.status === 402) {
        const body = await res.json();
        setMessages((m) => m.slice(0, -2));
        toast.error(body.detail, {
          action: { label: "Passer à Premium", onClick: () => (window.location.href = "/dashboard/parametres") },
        });
        return;
      }
      if (!res.ok) throw new Error("stream failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const ev = JSON.parse(part.slice(6));
          if (ev.delta) {
            acc += ev.delta;
            const current = acc;
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: current };
              return copy;
            });
          } else if (ev.error) {
            toast.error(ev.error);
          }
        }
      }
      await loadSessions();
    } catch {
      toast.error("Impossible d'envoyer le message. Veuillez réessayer.");
      setMessages((m) => (m.length && m[m.length - 1].content === "" ? m.slice(0, -2) : m));
    } finally {
      setSending(false);
    }
  };

  const freeQuota = quota && !quota.unlimited;

  return (
    <div className="mx-auto flex h-[calc(100vh-9.5rem)] max-w-6xl gap-4" data-testid={`page-chat-${agent}`}>
      <aside className="hidden w-60 shrink-0 flex-col md:flex">
        <button onClick={newSession} className={`flex items-center justify-center gap-2 rounded-xl py-3 text-xs font-bold text-white transition-all hover:-translate-y-0.5 ${accent.button}`} data-testid="chat-new-session">
          <MessageSquarePlus className="h-4 w-4" /> Nouvelle conversation
        </button>
        <div className="mt-3 flex-1 space-y-1.5 overflow-y-auto pr-1">
          {sessions.map((s) => (
            <button
              key={s.id}
              onClick={() => selectSession(s.id)}
              className={`group flex w-full items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-xs font-medium transition-colors ${
                activeId === s.id ? `${accent.activeSession}` : "border-slate-100 bg-white text-slate-600 hover:border-slate-200"
              }`}
              data-testid="chat-session-item"
            >
              <span className="flex-1 truncate">{s.title}</span>
              <Trash2 onClick={(e) => deleteSession(s.id, e)} className="h-3.5 w-3.5 shrink-0 text-slate-300 opacity-0 transition-opacity hover:!text-rouge group-hover:opacity-100" data-testid="chat-session-delete" />
            </button>
          ))}
        </div>
        {freeQuota && (
          <div className="mt-3 rounded-xl border border-slate-100 bg-white p-3.5" data-testid="chat-quota-chip">
            <p className="text-[11px] font-bold text-slate-700">{quota.remaining}/{quota.limit} messages gratuits aujourd'hui</p>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
              <div className={`h-full rounded-full ${accent.bar}`} style={{ width: `${(quota.remaining / quota.limit) * 100}%` }} />
            </div>
            <Link to="/dashboard/parametres" className="mt-2.5 flex items-center gap-1.5 text-[11px] font-bold text-amber-600 hover:underline" data-testid="chat-quota-upgrade-link">
              <Crown className="h-3 w-3" /> Illimité avec Premium — 7,99 €/mois
            </Link>
          </div>
        )}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-100 bg-slate-50/60">
        <header className="flex items-center gap-3 border-b border-slate-100 bg-white px-5 py-3.5">
          <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${accent.iconBg}`}><Icon className={`h-5 w-5 ${accent.iconColor}`} /></span>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-sm font-extrabold text-slate-900">{title}</h1>
            <p className="truncate text-[11px] text-slate-400">{subtitle}</p>
          </div>
          <button onClick={newSession} className="md:hidden" aria-label="Nouvelle conversation" data-testid="chat-new-session-mobile">
            <MessageSquarePlus className="h-5 w-5 text-slate-400" />
          </button>
          {user?.plan === "premium" && <span className="hidden items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-bold text-amber-700 sm:flex"><Crown className="h-3 w-3" /> Illimité</span>}
        </header>

        <div className="flex-1 space-y-3.5 overflow-y-auto px-4 py-5 sm:px-6">
          {loading ? (
            <div className="flex justify-center py-16"><LoaderCircle className="h-6 w-6 animate-spin text-slate-300" /></div>
          ) : messages.length === 0 ? (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="flex h-full flex-col items-center justify-center text-center">
              <span className={`flex h-16 w-16 items-center justify-center rounded-3xl ${accent.iconBg}`}><Icon className={`h-7 w-7 ${accent.iconColor}`} /></span>
              <p className="mt-4 flex items-center gap-1.5 text-sm font-bold text-slate-900"><Sparkles className="h-3.5 w-3.5 text-ambre" /> Comment puis-je vous aider ?</p>
              <p className="mt-1 max-w-sm text-xs text-slate-400">{subtitle}</p>
              <div className="mt-6 grid w-full max-w-lg gap-2 sm:grid-cols-2">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => send(s)} className="rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-left text-xs font-medium text-slate-600 transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-sm" data-testid="chat-suggestion-chip">
                    {s}
                  </button>
                ))}
              </div>
            </motion.div>
          ) : (
            messages.map((m, i) => (
              <Bubble key={m.id} role={m.role} content={m.content} accent={accent} streaming={sending && i === messages.length - 1 && m.role === "assistant"} />
            ))
          )}
          <div ref={bottomRef} />
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="flex items-end gap-2.5 border-t border-slate-100 bg-white p-3.5"
          data-testid="chat-input-form"
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1}
            placeholder="Posez votre question…"
            className="max-h-32 flex-1 resize-none rounded-xl border border-slate-200 px-4 py-3 text-sm placeholder:text-slate-400 focus:border-slate-400 focus:outline-none"
            data-testid="chat-input"
          />
          <button type="submit" disabled={sending || !input.trim()} className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white transition-all hover:-translate-y-0.5 disabled:opacity-40 ${accent.button}`} aria-label="Envoyer" data-testid="chat-send-button">
            {sending ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <SendHorizonal className="h-4 w-4" />}
          </button>
        </form>
      </div>
    </div>
  );
}
